import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { ArrowRight, Check, BookOpen, Sparkles, Mic, Camera, Brain, Heart, Star } from 'lucide-react';

const BRAND_LOGO = '/brand/logo.png';

// Inject SEO meta tags into <head> dynamically
function useSEO({ title, description, keywords, canonicalPath, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    // Canonical
    if (canonicalPath) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${window.location.origin}${canonicalPath}`);
    }

    // JSON-LD structured data
    let ldScript = document.head.querySelector('script[data-seo-ld]');
    if (ldScript) ldScript.remove();
    if (jsonLd) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.setAttribute('data-seo-ld', 'true');
      ldScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      // Keep title and meta when unmounting so back nav doesn't flicker
      if (ldScript && document.head.contains(ldScript)) ldScript.remove();
    };
  }, [title, description, keywords, canonicalPath, jsonLd]);
}

// Shows a row of "also covered in CBSE / ICSE / IB / State" links whenever
// this subject+class has content across multiple boards. Helps SEO internal linking.
function BoardSwitcher({ subjectSlug, classLevel, currentBoardSlug }) {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let ignore = false;
    apiGet('/public/seo/index').then(d => {
      if (ignore) return;
      const related = (d.items || []).filter(i =>
        i.subject_slug === subjectSlug && String(i.class_level) === String(classLevel)
      );
      setOptions(related);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [subjectSlug, classLevel]);

  if (options.length < 2) return null;

  return (
    <div className="mt-8 p-4 rounded-2xl bg-white/70 border border-slate-100" data-testid="seo-board-switcher">
      <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-slate-400 mb-2.5">Also available for</p>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const isCurrent = (opt.board_slug || null) === (currentBoardSlug || null);
          const label = opt.board_label || 'All boards';
          return (
            <Link key={opt.url} to={opt.url}
              data-testid={`seo-board-link-${opt.board_slug || 'all'}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading transition-all ${
                isCurrent
                  ? 'bg-slate-900 text-white cursor-default'
                  : 'bg-white border border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-600'
              }`}
              onClick={e => { if (isCurrent) e.preventDefault(); }}>
              {label}
              {!isCurrent && <ArrowRight className="w-3 h-3" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function SEOSubjectPage() {
  const { boardSlug, subjectSlug, classSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Parse class level from "class-10" → "10"
  const classLevel = (classSlug || '').replace(/^class-/, '');
  // Convert slug back to title-cased subject guess (the backend resolves actual subject by slug fallback)
  const subjectGuess = (subjectSlug || '').split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

  useEffect(() => {
    if (!subjectGuess || !classLevel) {
      setError('Invalid URL');
      return;
    }
    const boardQs = boardSlug ? `&board=${encodeURIComponent(boardSlug)}` : '';
    // Try exact match on guessed name
    apiGet(`/public/seo/page?subject=${encodeURIComponent(subjectGuess)}&class_level=${encodeURIComponent(classLevel)}${boardQs}`)
      .then(setData)
      .catch(async () => {
        // Fall back — fetch the index and try to find a close match by slug
        try {
          const idx = await apiGet('/public/seo/index');
          const match = (idx.items || []).find(i =>
            i.subject_slug === subjectSlug &&
            String(i.class_level) === String(classLevel) &&
            ((boardSlug && i.board_slug === boardSlug) || (!boardSlug && !i.board_slug))
          );
          if (match) {
            const bQ = match.board_slug ? `&board=${encodeURIComponent(match.board_slug)}` : '';
            const d = await apiGet(`/public/seo/page?subject=${encodeURIComponent(match.subject)}&class_level=${encodeURIComponent(match.class_level)}${bQ}`);
            setData(d);
          } else {
            setError('not-found');
          }
        } catch { setError('not-found'); }
      });
  }, [subjectGuess, classLevel, subjectSlug, boardSlug]);

  const jsonLd = data ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Course',
        name: `${data.subject} for ${data.class_label}`,
        description: data.description,
        provider: { '@type': 'Organization', name: 'Gurukul Veda', url: window.location.origin },
      },
      {
        '@type': 'FAQPage',
        mainEntity: (data.faqs || []).map(f => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  } : null;

  useSEO({
    title: data?.title,
    description: data?.description,
    keywords: (data?.keywords || []).join(', '),
    canonicalPath: data?.canonical_path,
    jsonLd,
  });

  if (error === 'not-found') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FEFBF4] px-6 text-center" data-testid="seo-not-found">
        <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-20 h-20 mb-6 opacity-80" />
        <h1 className="font-display text-3xl font-bold text-slate-900">We couldn't find that subject</h1>
        <p className="text-slate-500 mt-2 max-w-md">The combination "{subjectGuess} · Class {classLevel}" isn't in our curriculum yet. Browse everything we cover below.</p>
        <Link to="/learn" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 text-white font-heading text-sm" data-testid="seo-browse-all">
          Browse all subjects <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FEFBF4]">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleStart = () => navigate('/login');

  return (
    <div className="min-h-screen bg-[#FEFBF4] text-slate-800" data-testid="seo-subject-page" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-gradient-to-br from-indigo-300/20 via-cyan-200/15 to-transparent blur-3xl" />
        <div className="absolute top-80 -right-40 w-[35rem] h-[35rem] rounded-full bg-gradient-to-br from-amber-200/25 via-rose-200/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FEFBF4]/80 border-b border-slate-900/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="seo-header-logo">
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-display text-[17px] font-semibold text-slate-900 leading-tight tracking-tight">Gurukul Veda</p>
              <p className="text-[10px] text-slate-500 leading-tight -mt-0.5">Your Personal Study Companion</p>
            </div>
          </Link>
          <button onClick={handleStart} data-testid="seo-header-cta"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-heading hover:bg-slate-800 transition-all shadow-sm">
            Start free <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-10">
        <div className="max-w-3xl">
          <nav className="text-xs text-slate-500 font-body mb-4" data-testid="seo-breadcrumb">
            <Link to="/" className="hover:text-slate-800">Home</Link> · <Link to="/learn" className="hover:text-slate-800">Learn</Link>
            {data.board_label ? <> · <span className="text-slate-400">{data.board_label}</span></> : null}
            {' '}· <span className="text-slate-400">{data.class_label} · {data.subject}</span>
          </nav>
          <div className="flex items-center gap-2 mb-3">
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600">{data.class_label}</p>
            {data.board_label && (
              <span data-testid="seo-board-pill" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white text-[10px] font-heading uppercase tracking-wider">
                {data.board_label}
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold text-slate-900 leading-[1.02] tracking-[-0.035em]" data-testid="seo-h1">
            {data.board_label ? `${data.board_label} ` : ''}{data.subject} for {data.class_label}, <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">made personal.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-[1.65]">{data.description}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button onClick={handleStart} data-testid="seo-hero-cta"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-900 text-white text-sm font-heading font-semibold hover:bg-slate-800 transition-all shadow-lg">
              Start learning {data.subject} — it's free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <a href="#topics" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-slate-700 text-sm font-heading hover:bg-white transition-all">
              See all {data.topic_count} chapters
            </a>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-4 text-xs text-slate-500 font-body" data-testid="seo-trust-row">
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> CBSE · ICSE · IB · State</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> Free Basic forever</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> No credit card</span>
          </div>

          {/* Board switcher — shows only when more than one board has content for this subject+class */}
          <BoardSwitcher subjectSlug={subjectSlug} classLevel={classLevel} currentBoardSlug={data.board_slug} />
        </div>
      </section>

      {/* Features strip */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="seo-features-strip">
          {[
            { icon: Brain, label: 'AI Chat Tutor', color: 'text-indigo-500 bg-indigo-50' },
            { icon: Camera, label: 'Snap & Solve', color: 'text-emerald-500 bg-emerald-50' },
            { icon: Mic, label: 'Voice Tutor', color: 'text-sky-500 bg-sky-50' },
            { icon: Sparkles, label: 'Quizzes & Flashcards', color: 'text-amber-500 bg-amber-50' },
          ].map(f => (
            <div key={f.label} className="p-4 rounded-2xl bg-white/80 border border-slate-100 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${f.color}`}><f.icon className="w-4.5 h-4.5" /></div>
              <p className="font-heading text-sm text-slate-800">{f.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Topics grid */}
      <section id="topics" className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-5 h-5 text-indigo-500" />
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            All {data.topic_count} {data.subject} chapters for {data.class_label}
          </h2>
        </div>
        <p className="text-slate-500 mb-8 max-w-2xl">Tap any chapter after sign-up to start a warm, board-aligned tutor session. Every topic is mapped to your syllabus.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="seo-topics-grid">
          {data.topics.map((t, i) => (
            <button key={t.slug} onClick={handleStart} data-testid={`seo-topic-${t.slug}`}
              className="group text-left p-4 rounded-2xl bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center flex-shrink-0">
                  <span className="font-heading text-xs font-bold text-indigo-600">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors truncate">{t.name}</p>
                  <p className="text-[11px] text-slate-400 font-body">{data.subject} · {data.class_label}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Why parents love it */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="rounded-[2rem] p-8 sm:p-12 bg-gradient-to-br from-amber-50 via-orange-50/80 to-rose-50 border border-amber-100/80 shadow-xl">
          <div className="flex items-center gap-2 mb-3"><Heart className="w-5 h-5 text-rose-500" /><p className="font-heading text-[11px] uppercase tracking-[0.3em] text-rose-600">Why parents love it</p></div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight max-w-2xl">
            The tutor your child talks to after dinner — <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">by choice.</span>
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { title: 'No judgment', desc: 'Ask the silliest doubt — we answer warmly every time.' },
              { title: 'Syllabus-aware', desc: `Every ${data.subject} answer is aligned to ${data.class_label} curriculum.` },
              { title: 'Built for India', desc: 'English + Regional language voice tutors. CBSE · ICSE · IB · State.' },
            ].map(c => (
              <div key={c.title} className="p-5 rounded-2xl bg-white/70 border border-white">
                <p className="font-heading font-bold text-slate-900">{c.title}</p>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-12" data-testid="seo-faqs">
        <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight mb-8 text-center">Frequently asked</h2>
        <div className="space-y-3">
          {(data.faqs || []).map((f, i) => (
            <details key={i} className="group rounded-2xl bg-white border border-slate-100 p-5 hover:border-indigo-200 transition-colors" data-testid={`seo-faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                <span className="font-heading text-sm font-semibold text-slate-800">{f.q}</span>
                <span className="w-6 h-6 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 font-body leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Big CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="relative rounded-[2.5rem] bg-white/90 backdrop-blur-xl border border-slate-900/5 shadow-[0_20px_80px_-20px_rgba(30,41,59,0.2)] px-8 sm:px-14 py-14 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[30rem] h-[15rem] rounded-full bg-gradient-to-b from-indigo-200/40 to-transparent blur-3xl pointer-events-none" />
            <div className="relative">
              <Star className="w-10 h-10 mx-auto mb-4 text-amber-400 fill-current" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight text-slate-900 max-w-xl mx-auto">
                Start {data.subject} for {data.class_label} — it's free to try.
              </h2>
              <p className="mt-4 text-slate-600 max-w-lg mx-auto">Takes 10 seconds with Google. No credit card. Progress saves automatically.</p>
              <button onClick={handleStart} data-testid="seo-bigcta"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 text-white text-sm font-heading font-semibold shadow-xl hover:bg-slate-800 hover:scale-[1.02] transition-all">
                Get My Personal Tutor <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-900/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-8 h-8" />
            <p className="font-display text-base font-semibold text-slate-900">Gurukul Veda</p>
          </Link>
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <Link to="/learn" className="hover:text-slate-800">Browse subjects</Link>
            <Link to="/#pricing" className="hover:text-slate-800">Pricing</Link>
          </div>
          <p className="text-[11px] text-slate-400">© {new Date().getFullYear()} Gurukul Veda</p>
        </div>
      </footer>
    </div>
  );
}
